import {DatePipe} from '@angular/common';
import {Injectable} from '@angular/core';
import {FormatIbanPipe} from '@app/shared/pipe/format-iban-pipe';
import {FormatNumberPipe} from '@app/shared/pipe/format-number-pipe';
import {
  AdminBenutzer,
  Adresse,
  AdresseCreate,
  Bankverbindung,
  Benutzer,
  BenutzerStatus,
  FahrzeugResult,
  GebaeudeResult,
  Kontakt,
  MaklerTeam,
  PersonResult,
  SchadenReferent,
  SchadenResult,
  TodoResult
} from '@pezi/entities';

@Injectable()
export class StringUtil {
  private static formatIBANPipe: FormatIbanPipe = new FormatIbanPipe();
  private static formatNumberPipe: FormatNumberPipe = new FormatNumberPipe();
  private static datePipe: DatePipe = new DatePipe('de-AT');

  public static getAdresseDesc(adresse: Adresse, hideCountry?: boolean): string {
    if (adresse) {
      return adresse.plz + ' ' + adresse.ortsname + (!hideCountry ? ' ' + adresse.land : '') + ',\n' + adresse.strasse + ' ' + adresse.hausnr;
    } else {
      return 'Keine Adresse erfasst';
    }
  }

  public static convertDecimalStringToNumber(str: string): number {
    return parseFloat(str.replace(/\s/g, '')
      .replace(',', '.'));
  }

  public static onlyLeaveNumbersFromStr(str: string): string {
    return str.replace(/\D/g, '');
  }

  public static getDateStringFromDate(datum: Date): string { // Berücksichtigt Zeitzone
    if (datum) {
      const tzoffset = (datum).getTimezoneOffset() * 60000; //offset in milliseconds
      return (new Date(datum.getTime() - tzoffset)).toISOString()
        .substring(0, 10);
    }
    return null;
  }

  public static getDateTimeStringFromDate(datum: Date): string { // Berücksichtigt Zeitzone
    if (datum) {
      const tzoffset = (datum).getTimezoneOffset() * 60000; //offset in milliseconds
      return (new Date(datum.getTime() - tzoffset)).toISOString();
    }
    return null;
  }

  public static getGebaeudeDesc(geb: GebaeudeResult): string {
    if (geb) {
      return geb.name + (geb.vulgoName ? ' - ' + geb.vulgoName : '') + ',\n' +
        this.getAdresseDesc(geb.adresse);
    }
    return '-';
  }

  public static getAnwenwesenheitStr(bnStatus: BenutzerStatus): string {
    if (bnStatus) {
      switch (bnStatus.status) {
        case BenutzerStatus.STATUS_ANWESEND:
          return 'Anwesend';
        case BenutzerStatus.STATUS_ABWESEND:
          return 'Abwesend';
        case BenutzerStatus.STATUS_NA:
          return 'Anwesenheitsstatus unbekannt';
        case BenutzerStatus.STATUS_URLAUB:
          return 'Im Urlaub';
        case BenutzerStatus.STATUS_SEMINAR:
          return 'Seminar';

      }
    }
    return 'Anwesenheitsstatus unbekannt';
  }

  public static getGeschlechtBezeichnung(geschl: 'W' | 'M' | 'U' | any /* added "any" if there are more Genders added later*/): string {
    if (geschl) {
      switch (geschl) {
        case 'W':
          return 'weiblich';
        case 'M':
          return 'männlich';
        case 'U':
          return 'unbekannt';
        default:
          return 'unbekannt';
      }
    }
    return 'keine Angabe';
  }

  public static getFahrzeugDesc(fz: FahrzeugResult): string {
    if (fz) {
      return fz.kennzeichen + ', ' + (fz.marke ? fz.marke.value + ' ' : '') + fz.verkaufsbezeichnung + ',\n' +
        fz.leistung + ' kw' + ', ' + 'Baujahr: ' + fz.baujahr + ', Fahrgestellnummer: ' + fz.vin;
    }
    return '-';
  }

  public static getSchadenDesc(schaden: SchadenResult): string {
    if (schaden) {
      return this.formatNumberPipe.transform(schaden.schNr, 'schaden') + ', ' + 'Polizze: ' +
        this.formatNumberPipe.transform(schaden.polNr, 'polizze') +
        ',\n' + (schaden.sparte && schaden.sparte.value && schaden.sparte.key ? '[' + schaden.sparte.key.trim() + ']' + schaden.sparte.value : '') +
        ', Datum: ' + this.datePipe.transform(schaden.schadenDatum, 'dd.MM.yyyy') + ', Status: ' + schaden.status;
    }
    return '-';
  }

  public static getAdresseCreateDesc(adresse: AdresseCreate): string {
    if (adresse) {
      let str = adresse.plz + ' ' + adresse.ortsname + ',\n' +
        adresse.strasse;
      if (adresse.hausnrPac) {
        str += ' ' + adresse.hausnrPac;
      }
      if (adresse.hausnrTuerNr) {
        str += '/' + adresse.hausnrTuerNr;
      }
      if (adresse.hausnrRest) {
        str += '  ' + adresse.hausnrRest;
      }
      return str;
    } else {
      return 'Keine Adresse erfasst';
    }
  }

  public static getBankverbindungStr(bvb: Bankverbindung): string {
    if (bvb) {
      return this.formatIBANPipe.transform(bvb.iban) + (bvb.bankname ? (' ' + bvb.bankname) : '') + (bvb.kontoinhaber ? (` [${bvb.kontoinhaber}]`) : '');
    }
    return 'Keine Bankverbindung vorhanden';
  }

  public static getPersonDesc(person: PersonResult): string {
    if (person && person.nachname) {
      return person.nachname + (person.vorname ? ' ' + person.vorname : '') + (person.namensZusatz ? ' ' + person.namensZusatz : '');
    }
    return '';
  }

  public static getPersonDescWithAnrede(person: PersonResult): string {
    if (person && person.nachname) {
      if (person.anrede && person.anrede.value) {
        return person.anrede.value + ' ' + person.nachname + (person.vorname ? ' ' + person.vorname : '') + (person.namensZusatz ? ' ' + person.namensZusatz : '');
      }
      return person.nachname + (person.vorname ? ' ' + person.vorname : '') + (person.namensZusatz ? ' ' + person.namensZusatz : '');
    }
    return '';
  }

  public static getKontaktSubText(kontakt: Kontakt): string {
    let str = kontakt.fest ? kontakt.fest + ' -' : '';
    if (kontakt.kundenbuero) {
      return str + ' KB ' + kontakt.kundenbuero;
    }
    if (kontakt.zimmerNr) {
      return str + ' Zimmer Nr. ' + kontakt.zimmerNr
    }
    return str;
  }

  public static getBenutzerDesc(benutzer: Benutzer | SchadenReferent): string {
    if (benutzer && benutzer.name) {
      return benutzer.name + (benutzer.vorname ? ' ' + benutzer.vorname : '') + (benutzer.kontaktMobil ? ' / ' + benutzer.kontaktMobil + '(Mobil)' : '') +
        (benutzer.kontaktFest ? ' / ' + benutzer.kontaktFest + '(Festnetz)' : '') +
        (benutzer.kontaktEmail ? ' / ' + benutzer.kontaktEmail : '');
    }
    return '';
  }

  public static getBenutzerNameOnly(benutzer: Benutzer | SchadenReferent | AdminBenutzer): string {
    if (benutzer && benutzer.name) {
      return benutzer.name + (benutzer.vorname ? ' ' + benutzer.vorname : '');
    }
    return '';
  }

  public static getTodoStatusDesc(todo: TodoResult): string {
    if (todo.status) {
      switch (todo.status) {
        case TodoResult.STATUS_BEARBEITET:
          return 'Bearbeitet';
        case TodoResult.STATUS_DRINGEND:
          return 'Dringend';
        case TodoResult.STATUS_ERLEDIGT:
          return 'Erledigt';
        case TodoResult.STATUS_ERSTELLT:
          return 'Erstellt';
        case TodoResult.STATUS_TRASH:
          return 'Gelöscht';
        default:
          return 'Keine Angabe';
      }
    }
    return 'Keine Angabe';
  }

  public static getTodoStatusColorClass(todo: TodoResult): string {
    if (todo.status) {
      switch (todo.status) {
        case TodoResult.STATUS_BEARBEITET:
          return 'status_primary';
        case TodoResult.STATUS_DRINGEND:
          return 'status_red';
        case TodoResult.STATUS_ERLEDIGT:
          return 'status_erledigt';
        case TodoResult.STATUS_ERSTELLT:
          return 'status_primary';
        case TodoResult.STATUS_TRASH:
          return 'status_gray';
        default:
          return 'status_gray';
      }
    }
    return 'status_gray';
  }

  public static getPersonDescWithTitle(person: PersonResult | MaklerTeam | Kontakt): string {
    if (person && person.nachname) {
      const titelPre = person.titelPre ? person.titelPre + ' ' : '';
      const titelPost = person.titelPost ? ' ' + person.titelPost : '';
      return titelPre + person.nachname + ' ' + (person.vorname ? person.vorname : '') + ((person as PersonResult).namensZusatz ? ' ' + (person as PersonResult).namensZusatz : '') + titelPost;
    }
    return '';
  }

  public static getSignalCompliantNumber(mobil: string): string {
    if (!mobil) {
      return '';
    }
    let nummer = mobil.replace(/[^+^0-9]/g, '');
    nummer = nummer.replace(/^0043/, '+43');
    nummer = nummer.replace(/^0/, '+43');

    return nummer;
  }
}
